import { useState, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { storesApi } from '../services/api';
import { Map } from '../components/Map';
import type { PlaceSearchResult, StoreCreateRequest } from 'shared';

type SortOption = 'relevance' | 'distance' | 'name';

// Monthly activity sparkline from review timestamps
function MonthlyActivity({ reviews }: { reviews: Array<{ publishTime: string }> | undefined }) {
  if (!reviews || reviews.length === 0) return null;

  // Get last 12 months
  const now = new Date();
  const months: { label: string; count: number; key: string }[] = [];

  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'short' });
    months.push({ label, count: 0, key });
  }

  // Count reviews per month
  reviews.forEach((r) => {
    const date = new Date(r.publishTime);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const month = months.find((m) => m.key === key);
    if (month) month.count++;
  });

  const maxCount = Math.max(...months.map((m) => m.count), 1);
  const totalReviews = months.reduce((sum, m) => sum + m.count, 0);

  if (totalReviews === 0) return null;

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-600">Review Activity (12 mo)</span>
        <span className="text-xs text-gray-400">{totalReviews} reviews</span>
      </div>
      <div className="flex items-end gap-0.5 h-8">
        {months.map((m) => (
          <div
            key={m.key}
            className="flex-1 bg-blue-400 rounded-t-sm min-h-[2px] hover:bg-blue-500 transition-colors"
            style={{ height: `${(m.count / maxCount) * 100}%` }}
            title={`${m.label}: ${m.count} reviews`}
          />
        ))}
      </div>
      <div className="flex justify-between text-[9px] text-gray-400 mt-1">
        <span>{months[0].label}</span>
        <span>{months[11].label}</span>
      </div>
    </div>
  );
}

function getDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Cache key for persisting search state
const SEARCH_CACHE_KEY = 'store-finder-search';

interface SearchCache {
  query: string;
  results: PlaceSearchResult[];
}

export function StoreFinder() {
  const queryClient = useQueryClient();

  // Restore cached search state on mount
  const cachedSearch = queryClient.getQueryData<SearchCache>([SEARCH_CACHE_KEY]);

  const [searchQuery, setSearchQuery] = useState(cachedSearch?.query || '');
  const [searchResults, setSearchResults] = useState<PlaceSearchResult[]>(cachedSearch?.results || []);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedResult, setSelectedResult] = useState<PlaceSearchResult | null>(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('relevance');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    name: '',
    chain_name: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
  });

  const [suggestions, setSuggestions] = useState<PlaceSearchResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const resultRefs = useRef<Record<string, HTMLDivElement>>({});
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Cache search state when it changes
  useEffect(() => {
    if (searchQuery || searchResults.length > 0) {
      queryClient.setQueryData<SearchCache>([SEARCH_CACHE_KEY], {
        query: searchQuery,
        results: searchResults,
      });
    }
  }, [searchQuery, searchResults, queryClient]);

  // Debounced auto-search as user types
  useEffect(() => {
    if (searchQuery.trim().length < 3) return;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        await searchMutation.mutateAsync(searchQuery);
      } finally {
        setIsSearching(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounced autocomplete suggestions
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoadingSuggestions(true);
    const timer = setTimeout(async () => {
      try {
        const results = await storesApi.search(q, userLocation || undefined);
        setSuggestions(results.slice(0, 5));
        setShowSuggestions(results.length > 0);
      } catch {
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      setIsLoadingSuggestions(false);
    };
  }, [searchQuery, userLocation]);

  const handleSuggestionClick = (suggestion: PlaceSearchResult) => {
    setSearchQuery(suggestion.name);
    setSearchResults(suggestions);
    setSelectedResult(suggestion);
    setShowSuggestions(false);
    if (suggestions.length > 0 && window.innerWidth < 1024) {
      setSidebarOpen(true);
    }
  };

  // Scroll selected result into view
  useEffect(() => {
    if (selectedResult) {
      const el = resultRefs.current[selectedResult.place_id];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedResult]);

  // Sort results
  const sortedResults = useMemo(() => {
    if (sortBy === 'relevance') return searchResults;

    return [...searchResults].sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === 'distance' && userLocation) {
        const distA = getDistance(userLocation.lat, userLocation.lng, a.lat, a.lng);
        const distB = getDistance(userLocation.lat, userLocation.lng, b.lat, b.lng);
        return distA - distB;
      }
      return 0;
    });
  }, [searchResults, sortBy, userLocation]);

  const searchMutation = useMutation({
    mutationFn: async (query: string) => {
      return storesApi.search(query, userLocation || undefined);
    },
    onSuccess: (data) => {
      setSearchResults(data);
      setSelectedResult(null);
      // Open sidebar on mobile if we have results
      if (data.length > 0 && window.innerWidth < 1024) {
        setSidebarOpen(true);
      }
    },
  });

  const addStoreMutation = useMutation({
    mutationFn: (data: StoreCreateRequest) => storesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    },
  });

  // Fetch place details for ALL search results
  const placeDetailsQueries = useQueries({
    queries: searchResults.map((result) => ({
      queryKey: ['place-details', result.place_id],
      queryFn: () => storesApi.getPlaceDetails(result.place_id),
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      enabled: searchResults.length > 0,
    })),
  });

  // Create a map of place_id -> details for easy lookup
  const placeDetailsMap = useMemo(() => {
    const map: Record<string, { reviews: Array<{ publishTime: string }> } | undefined> = {};
    searchResults.forEach((result, index) => {
      const query = placeDetailsQueries[index];
      if (query?.data) {
        map[result.place_id] = query.data;
      }
    });
    return map;
  }, [searchResults, placeDetailsQueries]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      await searchMutation.mutateAsync(searchQuery);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddFromSearch = (result: PlaceSearchResult) => {
    addStoreMutation.mutate({
      name: result.name,
      lat: result.lat,
      lng: result.lng,
      place_id: result.place_id,
      address_line1: result.address,
    });
    setSearchResults((prev) => prev.filter((r) => r.place_id !== result.place_id));
    setSelectedResult(null);
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualForm.name || !manualForm.address_line1) return;
    addStoreMutation.mutate({
      name: manualForm.name,
      chain_name: manualForm.chain_name || undefined,
      address_line1: manualForm.address_line1,
      city: manualForm.city || undefined,
      state: manualForm.state || undefined,
      postal_code: manualForm.postal_code || undefined,
    });
    setManualForm({
      name: '',
      chain_name: '',
      address_line1: '',
      city: '',
      state: '',
      postal_code: '',
    });
    setShowManualForm(false);
  };

  const handleResultSelect = (result: PlaceSearchResult) => {
    setSelectedResult(result);
    // Close sidebar on mobile to show the selected marker on map
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Results list component (reused in sidebar and mobile list view)
  const ResultsList = ({ className = '' }: { className?: string }) => (
    <div className={`overflow-y-auto ${className}`}>
      {sortedResults.length > 0 ? (
        <div className="divide-y divide-gray-100">
          {sortedResults.map((result) => {
            const isSelected = selectedResult?.place_id === result.place_id;
            const distance = userLocation
              ? getDistance(userLocation.lat, userLocation.lng, result.lat, result.lng)
              : null;

            return (
              <div
                key={result.place_id}
                ref={(el) => {
                  if (el) resultRefs.current[result.place_id] = el;
                }}
                onClick={() => handleResultSelect(result)}
                className={`p-3 sm:p-4 cursor-pointer transition-all ${
                  isSelected
                    ? 'bg-blue-50 border-l-4 border-l-blue-600 shadow-sm'
                    : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                      {result.name}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{result.address}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      {result.rating && (
                        <div className="flex items-center gap-1" title="Google Maps rating">
                          <span className="text-xs text-gray-400">Rating</span>
                          <span className="text-yellow-500 text-sm">★</span>
                          <span className="text-sm font-medium text-gray-700">{result.rating.toFixed(1)}</span>
                          {result.review_count && (
                            <span className="text-xs text-gray-400">({result.review_count} reviews)</span>
                          )}
                        </div>
                      )}
                      {distance !== null && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">Distance</span>
                          <span className="text-xs font-medium text-gray-600">
                            {distance < 0.1 ? '<0.1' : distance.toFixed(1)} mi
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddFromSearch(result);
                    }}
                    disabled={addStoreMutation.isPending}
                    className="shrink-0 px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors active:scale-95"
                  >
                    + Add
                  </button>
                </div>
                {/* Show activity chart for all results */}
                {placeDetailsMap[result.place_id] ? (
                  <MonthlyActivity reviews={placeDetailsMap[result.place_id]?.reviews} />
                ) : (
                  <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Loading activity...
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : searchMutation.isSuccess ? (
        <div className="p-8 text-center text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm">No stores found</p>
          <p className="text-xs mt-1">Try a different search term</p>
        </div>
      ) : (
        <div className="p-8 text-center text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-sm">Search for stores</p>
          <p className="text-xs mt-1">Results will appear here</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row h-full relative">
      
      {/* Desktop: Left Sidebar */}
      <div className="hidden lg:flex w-96 bg-white border-r border-gray-200 flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Find Stores</h2>
        </div>

        {/* Search Form */}
        <div className="p-4 border-b border-gray-200">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Search stores (e.g., Target near Dallas)"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <svg
                className="absolute left-3 top-3 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {isLoadingSuggestions && (
                <svg className="absolute right-3 top-3 w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                  {suggestions.map((s) => (
                    <button
                      key={s.place_id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(s); }}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                      <p className="text-xs text-gray-500 truncate">{s.address}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={isSearching || !searchQuery.trim()}
              className="w-full mt-3 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              {isSearching ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Searching...
                </span>
              ) : (
                'Search'
              )}
            </button>
          </form>
        </div>

        {/* Sort Options */}
        {searchResults.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
            <span className="text-xs text-gray-500">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="relevance">Relevance</option>
              <option value="distance" disabled={!userLocation}>
                Distance {!userLocation && '(need location)'}
              </option>
              <option value="name">Name (A-Z)</option>
            </select>
            <span className="text-xs text-gray-400 ml-auto">
              {searchResults.length} results
            </span>
          </div>
        )}

        {/* Results List */}
        <ResultsList className="flex-1" />

        {/* Manual Entry Toggle */}
        <div className="p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setShowManualForm(!showManualForm)}
            className="w-full text-left text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {showManualForm ? 'Hide manual entry' : 'Add store manually'}
          </button>

          {showManualForm && (
            <form onSubmit={handleManualSubmit} className="mt-4 space-y-3">
              <input
                type="text"
                value={manualForm.name}
                onChange={(e) => setManualForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Store name *"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                value={manualForm.chain_name}
                onChange={(e) => setManualForm((prev) => ({ ...prev, chain_name: e.target.value }))}
                placeholder="Chain name (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                value={manualForm.address_line1}
                onChange={(e) => setManualForm((prev) => ({ ...prev, address_line1: e.target.value }))}
                placeholder="Address *"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  value={manualForm.city}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  value={manualForm.state}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, state: e.target.value }))}
                  placeholder="State"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  value={manualForm.postal_code}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, postal_code: e.target.value }))}
                  placeholder="ZIP"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={addStoreMutation.isPending || !manualForm.name || !manualForm.address_line1}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium transition-colors"
              >
                Add Store
              </button>
            </form>
          )}

          {addStoreMutation.isSuccess && (
            <p className="mt-3 text-green-600 text-sm text-center">Store added!</p>
          )}
        </div>
      </div>

      {/* Mobile: Sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-[1000]"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile: Slide-out Sidebar */}
      <div className={`
        lg:hidden fixed inset-y-0 left-0 z-[1001]
        w-80 max-w-[85vw] bg-white shadow-xl
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col
      `}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Results</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Sort bar */}
        {searchResults.length > 0 && (
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
            <span className="text-xs text-gray-500">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:ring-1 focus:ring-blue-500"
            >
              <option value="relevance">Relevance</option>
              <option value="distance" disabled={!userLocation}>Distance</option>
              <option value="name">Name (A-Z)</option>
            </select>
            <span className="text-xs text-gray-400 ml-auto">{searchResults.length}</span>
          </div>
        )}

        {/* Results List */}
        <ResultsList className="flex-1" />
      </div>

      {/* Mobile: Search bar - bottom center */}
      <div className="lg:hidden absolute bottom-4 left-3 right-3 z-[1001]">
        {showSuggestions && suggestions.length > 0 && (
          <div className="mb-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {suggestions.map((s) => (
              <button
                key={s.place_id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(s); }}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
              >
                <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                <p className="text-xs text-gray-500 truncate">{s.address}</p>
              </button>
            ))}
          </div>
        )}
        <form onSubmit={handleSearch} className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl shadow-lg">
          <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="Search stores..."
            className="flex-1 py-1 bg-transparent text-base outline-none"
          />
          {searchQuery.trim() && (
            <button
              type="submit"
              disabled={isSearching}
              className="p-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
            >
              {isSearching ? (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              )}
            </button>
          )}
          {searchResults.length > 0 && (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-medium"
            >
              {searchResults.length}
            </button>
          )}
        </form>
      </div>

      {/* Map - Always rendered and visible */}
      <div className="flex-1 relative">
        <Map
          markers={sortedResults.map((r) => ({
            id: r.place_id,
            lat: r.lat,
            lng: r.lng,
            name: r.name,
            address: r.address,
            photoUrl: r.photo_url,
            selected: selectedResult?.place_id === r.place_id,
          }))}
          autoLocate
          fitToMarkers
          onMarkerClick={(id) => {
            const result = searchResults.find((r) => r.place_id === id);
            if (result) setSelectedResult(result);
          }}
          onLocationFound={setUserLocation}
        />
      </div>
    </div>
  );
}
